import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Heading from "@tiptap/extension-heading";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Blockquote from "@tiptap/extension-blockquote";
import CodeBlock from "@tiptap/extension-code-block";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { Extension } from "@tiptap/core";
import type { Mark } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import CitationNode from "@/components/FpAppV4/CitationNode";
import NumericCitationNode from "@/components/FpAppV4/NumericCitationNode";
import ShowWorkNode from "@/components/FpAppV4/ShowWorkNode";
import ChartDataNode from "@/components/FpAppV4/ChartDataNode";
import QueryInputNode from "@/components/FpEditor/Nodes/QueryInputNode";
import ToolResponseNode from "@/components/FpAppV4/ToolResponseNode";
import EditorHighlightNumberNode from "@/components/FpAppV4/EditorHighlightNumberNode";
import MultipleCitationsNode from "@/components/FpAppV4/MultipleCitationsNode";
import QueryTextNode from "@/components/FpAppV4/QueryTextNode";
import AnswerAddedNode from "@/components/FpEditor/Nodes/AnswerAddedNode";
import { tableExtensions } from "./tableExtensions";
import { CommentThreadMark } from "./commentMark";
import EditorTableNode from "@/components/FpAppV4/EditorTableNode";
import ChartNode from "../Nodes/ChartNode";
import DocumentsUsedListNode from "../Nodes/DocumentsUsedListNode";
import CalculationDetailsNode from "../Nodes/CalculationDetailsNode";
import QueryInfoNode from "../Nodes/QueryInfoNode";
import { checkAndHandleMatrixCellCitation } from "@/components/common/MarkDownRenderer";
// Routed through @tiptap/pm rather than the bare prosemirror-state package
// (#11904): that one is not a declared dependency and resolves only by
// hoisting, so a second ProseMirror copy in the tree would make every
// PluginKey built here an object the editor never matches.
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";
import store from "@/redux/store";

declare module "@tiptap/core" {
  /**
   * Whether this editor is a plain-text Document rather than a report
   * (#11548 round 16). Written by EditorMain before creation and read by
   * extensions that have no React context to reach for: the placeholder
   * below, and QueryInputNode's "\queryInput" input rule.
   *
   * Declared here because v3's `Storage` is an empty interface meant to be
   * augmented; without this every reader needs a cast, which is what the
   * v2 code did (#11904).
   */
  interface Storage {
    fpPlainDocument?: boolean;
  }
}

/**
 * Convert a File or Blob to a base64 data URL
 */
const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Fetch an image from a URL and convert it to base64
 */
const urlToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return await fileToBase64(blob);
};

/**
 * Check if a URL is fetchable (http/https). Local file:// URLs and other
 * schemes (e.g. from LibreOffice temp files) cannot be fetched by the browser.
 */
const isFetchableUrl = (src: string): boolean => {
  return src.startsWith("http://") || src.startsWith("https://");
};

/**
 * Check if HTML content has meaningful text content (not just images)
 * This helps determine if we should prioritize HTML over image clipboard data
 */
const hasTextContent = (html: string): boolean => {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(html, "text/html");
  // Get text content, trim whitespace, and check if there's actual text
  const textContent = doc.body.textContent?.trim() || "";
  return textContent.length > 0;
};

/**
 * Import query_answer documents from a source workspace to the current workspace.
 * Returns true on success, false on failure (e.g. cross-org rejection).
 */
const importQueryAnswers = async (
  qidMapping: Record<string, string>,
  sourceWorkspaceId: string
): Promise<boolean> => {
  const destinationWorkspaceId = store.getState().fpAppV4Data.workspaceId;
  if (!destinationWorkspaceId) return false;
  try {
    const response = await fpFetch(
      `${BASE_API_URL}/workspaces/importQueryAnswers`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          destinationWorkspaceId,
          sourceWorkspaceId,
          qidMapping,
        }),
      }
    );
    return response?.ok ?? false;
  } catch (error) {
    console.error("Failed to import query answers:", error);
    return false;
  }
};

/**
 * Extension to handle paste from Microsoft Word on macOS.
 * Word places content in multiple clipboard formats (HTML, RTF, plain text, and image).
 * On macOS, TipTap may prioritize the image format, causing text to paste as an image.
 * This extension prioritizes HTML content with text over image data.
 */
const WordPasteHandler = Extension.create({
  name: "wordPasteHandler",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("wordPasteHandler"),
        props: {
          transformPastedHTML: (html) => {
            const domParser = new DOMParser();
            const doc = domParser.parseFromString(html, "text/html");
            const images = doc.querySelectorAll("img");
            let modified = false;

            images.forEach((img) => {
              const src = img.getAttribute("src");
              if (src && !src.startsWith("data:") && !isFetchableUrl(src)) {
                img.remove();
                modified = true;
              }
            });

            return modified ? doc.body.innerHTML : html;
          },

          handlePaste: (view, event) => {
            const html = event.clipboardData?.getData("text/html");

            // Handle paste with query answer references
            if (html) {
              const domParser = new DOMParser();
              const doc = domParser.parseFromString(html, "text/html");
              const mapTemplate = doc.querySelector(
                "template[data-fp-qid-map]"
              );

              if (mapTemplate) {
                event.preventDefault();

                let mapData: {
                  qidMapping?: Record<string, string>;
                  sourceWorkspaceId?: string;
                } = {};
                try {
                  mapData = JSON.parse(
                    mapTemplate.getAttribute("data-fp-qid-map") || "{}"
                  );
                } catch {
                  // Invalid JSON, ignore mapping
                }
                mapTemplate.remove();

                // Clean non-fetchable images
                doc.querySelectorAll("img").forEach((img) => {
                  const src = img.getAttribute("src");
                  if (src && !src.startsWith("data:") && !isFetchableUrl(src)) {
                    img.remove();
                  }
                });

                const doImportAndInsert = async () => {
                  let importSucceeded = true;
                  if (
                    mapData.qidMapping &&
                    mapData.sourceWorkspaceId &&
                    Object.keys(mapData.qidMapping).length > 0
                  ) {
                    importSucceeded = await importQueryAnswers(
                      mapData.qidMapping,
                      mapData.sourceWorkspaceId
                    );
                  }

                  if (!importSucceeded) {
                    // Cross-org or failed import: strip all qid elements
                    doc.querySelectorAll("[qid]").forEach((el) => el.remove());
                  }

                  const { schema, tr } = view.state;
                  const pmParser = ProseMirrorDOMParser.fromSchema(schema);
                  const slice = pmParser.parseSlice(doc.body);
                  tr.replaceSelection(slice);
                  view.dispatch(tr);
                };

                doImportAndInsert();
                return true;
              }
            }

            const items = Array.from(event.clipboardData?.items || []);

            // Check if we have HTML content with actual text
            // If so, prioritize HTML over any image data (fixes Word paste on macOS)
            const htmlHasText = html && hasTextContent(html);

            // If clipboard has both image and HTML with text, prioritize HTML
            const hasImageItem = items.some(
              (item) => item.type.indexOf("image") === 0
            );

            if (htmlHasText && hasImageItem) {
              const domParser = new DOMParser();
              const doc = domParser.parseFromString(html, "text/html");
              const images = doc.querySelectorAll("img");

              if (images.length > 0) {
                const hasFetchableImages = Array.from(images).some((img) => {
                  const src = img.getAttribute("src");
                  return src && isFetchableUrl(src);
                });

                if (!hasFetchableImages) {
                  // No fetchable images - let TipTap's default paste preserve
                  // formatting; transformPastedHTML will strip broken images
                  return false;
                }

                event.preventDefault();

                Promise.all(
                  Array.from(images).map(async (img) => {
                    const src = img.getAttribute("src");
                    if (
                      src &&
                      !src.startsWith("data:") &&
                      isFetchableUrl(src)
                    ) {
                      try {
                        const base64 = await urlToBase64(src);
                        img.setAttribute("src", base64);
                      } catch (error) {
                        console.error("Failed to convert image URL:", src);
                      }
                    }
                  })
                )
                  .then(() => {
                    const { schema, tr } = view.state;
                    const pmParser = ProseMirrorDOMParser.fromSchema(schema);
                    const slice = pmParser.parseSlice(doc.body);
                    tr.replaceSelection(slice);
                    view.dispatch(tr);
                  })
                  .catch((error) => {
                    console.error("Failed to process pasted content:", error);
                  });

                return true;
              }
            }

            return false;
          },
        },
      }),
    ];
  },
});

const CustomPlaceholder = Placeholder.configure({
  placeholder: ({ editor, node }) => {
    if (node.type.name === "paragraph") {
      // A Document is plain text (#11548 round 16): "/" doesn't prompt
      // there, so the placeholder must not advertise it. EditorMain stamps
      // the mode onto editor.storage before creation.
      const plainDocument = Boolean(editor.storage.fpPlainDocument);
      return plainDocument
        ? "Start writing..."
        : "Type / to prompt Finpilot...";
    }
    return "";
  },
  showOnlyWhenEditable: true,
  showOnlyCurrent: true,
});

const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-node-id"),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }
          return {
            "data-node-id": attributes.id,
          };
        },
      },
    };
  },
});

/**
 * Custom Link extension that opens links in new tabs
 * Link stops extending after space
 * Also handles citation links (with cid= and match= params) specially for matrix cell citations
 */
const CustomLink = Link.configure({
  openOnClick: true,
  autolink: false,
  HTMLAttributes: {
    target: "_blank",
    rel: "noopener noreferrer",
  },
}).extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("citationLinkHandler"),
        props: {
          handleClick: (view: EditorView, pos: number, event: MouseEvent) => {
            const { state } = view;
            const { doc } = state;
            const $pos = doc.resolve(pos);

            // Check if we clicked on a link mark
            const linkMark = $pos
              .marks()
              .find((mark: Mark) => mark.type.name === "link");
            if (!linkMark) {
              return false;
            }

            const href = linkMark.attrs.href;

            // Check if this is a citation link (has cid= and match= parameters)
            if (href && href.includes("cid=") && href.includes("match=")) {
              try {
                const url = new URL(href);
                const citationId = url.searchParams.get("cid");
                const matchText = url.searchParams.get("match");
                const qid = url.searchParams.get("qid");

                if (citationId && matchText && qid) {
                  // Prevent default link opening
                  event.preventDefault();
                  event.stopPropagation();

                  // Check if this is a matrix cell citation
                  checkAndHandleMatrixCellCitation(citationId, qid, matchText)
                    .then((isMatrixCellCitation) => {
                      if (!isMatrixCellCitation) {
                        // Not a matrix cell citation, open document panel
                        window.dispatchEvent(
                          new CustomEvent("openDocumentPanel", {
                            detail: {
                              citationId,
                              queryId: qid,
                              originalNumValue: matchText,
                            },
                          })
                        );
                      }
                      // If it's a matrix cell citation, checkAndHandleMatrixCellCitation
                      // already dispatched the highlightMatrixCell event
                    })
                    .catch((error) => {
                      console.error("Error handling citation link:", error);
                    });

                  return true; // Handled the click
                }
              } catch (error) {
                // If URL parsing fails, let the default link behavior handle it
                console.error("Error parsing citation link URL:", error);
              }
            }

            // For non-citation links, let the default behavior handle it
            return false;
          },
        },
      }),
    ];
  },
  addKeyboardShortcuts() {
    return {
      Space: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $from } = selection;

        // Check if cursor is at the end of a link
        const linkMark = $from
          .marks()
          .find((mark) => mark.type.name === "link");

        if (linkMark) {
          // Remove the link mark before inserting the space
          return this.editor
            .chain()
            .command(({ tr }) => {
              tr.removeStoredMark(this.editor.schema.marks.link);
              return true;
            })
            .insertContent(" ")
            .run();
        }

        return false;
      },
    };
  },
});

const editorExtensions = [
  StarterKit.configure({
    heading: false,
    bold: false,
    italic: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    codeBlock: false,
    blockquote: false,
    // StarterKit bundles Link and Underline as of tiptap v3; this list
    // registers its own of each below, and two extensions cannot share a
    // name — tiptap only warns and drops one, which would cost the report
    // editor target="_blank", the citation-link click handler and the
    // Space-exits-link shortcut (#11904).
    link: false,
    underline: false,
    // Also new in v3: TrailingNode appends an empty paragraph to any
    // document not ending in one. EditorMain autosaves on update, so
    // leaving it on rewrites a report the moment it is opened. Turning it
    // back on is a product call, not a dependency-bump call.
    trailingNode: false,
    // Likewise new in v3: ListKeymap rebinds Backspace and Delete inside
    // lists. It changes no content, but it is still an editing-behaviour
    // change this migration has no mandate to make.
    listKeymap: false,
    paragraph: {},
  }),
  Bold,
  Italic,
  Underline,
  CustomHeading,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  CodeBlock,
  QueryInputNode,
  ToolResponseNode,
  CitationNode,
  NumericCitationNode,
  EditorTableNode,
  EditorHighlightNumberNode,
  CalculationDetailsNode,
  ...tableExtensions,
  ShowWorkNode,
  CustomPlaceholder,
  QueryTextNode,
  MultipleCitationsNode,
  AnswerAddedNode,
  DocumentsUsedListNode,
  QueryInfoNode,
  Image.configure({
    inline: true,
    allowBase64: true,
  }),
  CustomLink,
  // Document comments (#13194): the anchor mark and its highlights.
  CommentThreadMark,
  ChartNode,
  WordPasteHandler,
];

export default editorExtensions;
